import { useState, useEffect, useRef, useMemo } from 'react';
import Map from './map';
import FeaturePopupCard from './components/feature-popup-card';
import UserPopupCard from './components/user-popup-card';
import FeatureDetailsModal from './components/feature-details-modal';
import UserDetailsModal from './components/user-details-modal';
import { DesktopProfileModal } from 'src/pages/profile'
import { api, useAuth, requestLocationPermission } from 'src/utils';
import { useNavigate } from 'react-router-dom';
import { useIsMobile, useSnackbar } from 'src/hooks';
import { ALL_TYPES, ALL_COUNTRIES } from 'src/data';
import MapPageLayout from './map-page-layout';
import { useMapStore, useFeatureTravellerStore, useMapFiltersStore } from 'src/stores';
import { CallToActionHero } from 'src/components';

import MapFeatureVideo from 'src/assets/videos/features/map-hero.mp4';

export default function MapContainer(props) {
    const [placeDetailsOpen, setPlaceDetailsOpen] = useState(false);
    const [userDetailsOpen, setUserDetailsOpen] = useState(false);
    const [displayLogin, setDisplayLogin] = useState(false);
    const [favouriteLoading, setFavouriteLoading] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);
    const [mobileDrawerState, setMobileDrawerState] = useState("collapsed");
    const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);

    const selectedTypes = useMapFiltersStore((state) => state.selectedTypes);
    const setSelectedTypes = useMapFiltersStore((state) => state.setSelectedTypes);
    const selectedCountries = useMapFiltersStore((state) => state.selectedCountries);
    const setSelectedCountries = useMapFiltersStore((state) => state.setSelectedCountries);
    const showFavouritesOnly = useMapFiltersStore((state) => state.showFavouritesOnly);
    const setShowFavouritesOnly = useMapFiltersStore((state) => state.setShowFavouritesOnly);

    const features = useFeatureTravellerStore((state) => state.features);
    const setFeatures = useFeatureTravellerStore((state) => state.setFeatures);
    const selectedFeature = useFeatureTravellerStore((state) => state.selectedFeature);
    const setSelectedFeature = useFeatureTravellerStore((state) => state.setSelectedFeature);
    const selectedTraveller = useFeatureTravellerStore((state) => state.selectedTraveller);
    const setSelectedTraveller = useFeatureTravellerStore((state) => state.setSelectedTraveller);
    const setTravellers = useFeatureTravellerStore((state) => state.setTravellers);
    const favouriteIds = useFeatureTravellerStore((state) => state.favouriteIds);
    const setFavouriteIds = useFeatureTravellerStore((state) => state.setFavouriteIds);

    const hasRequestedUsers = useRef(false);
    const hasRequestedFavourites = useRef(false);
    const showDrawer = !selectedFeature && !selectedTraveller;
    const [showDrawerView, setShowDrawerView] = useState(false);
    const isDesktopViewExpanded = showDrawerView && showDrawer;

    const { user } = useAuth();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const timer = useRef();
    const { openSnackbar } = useSnackbar();
    const { flyTo } = useMapStore();

    useEffect(() => {
        return () => {
            clearTimeout(timer.current);
        };
    }, []);

    useEffect(() => {
        setSelectedTypes(getAllowedTypes(user?.role));
    }, [user?.role]);

    useEffect(() => {
        const loadPlaces = async () => {
            try {
                const res = await fetch('/map-features.json');
                const json = await res.json();
                setFeatures(json.features || []);
            } catch (err) {
                console.error("Failed to load places", err);
            }
        };
        loadPlaces();
    }, []);

    useEffect(() => {
        if (!user || hasRequestedFavourites.current) return;
        hasRequestedFavourites.current = true;
        const fetchFavourites = async () => {
            try {
                const res = await api.get('/user/favourites');
                const data = res.data;
                setFavouriteIds(data); 
            } catch (err) {
                console.error("Error fetching favourites", err);
            }
        };

        fetchFavourites();
    }, [user]);

    useEffect(() => {
        if (!user || hasRequestedUsers.current) return;
        hasRequestedUsers.current = true;
        updateLocationAndFetchUsers();
    }, [user]);

    useEffect(() => {
        if (!user) {
            setTravellers([]);
            setSelectedTraveller(null);
            hasRequestedUsers.current = false;
        }
    }, [user]);

    const handleShowLocations = () => {
        if (selectedTypes.length === 0 && selectedCountries.length === 0) {
            setSelectedTypes(getAllowedTypes(user?.role));
            setSelectedCountries(ALL_COUNTRIES.map(c => c.name));
        } else {
            setSelectedTypes([]);
            setSelectedCountries([]);
        }
    };

    const toggleSelectedTypes = () => {
        setSelectedTypes(selectedTypes?.length === 0 ? getAllowedTypes(user?.role) : []);
    };

    const updateLocationAndFetchUsers = async () => {
        try {
            setUsersLoading(true);

            const coords = await requestLocationPermission();
            setLocationPermissionDenied(false);

            const locationCoords = [coords.longitude, coords.latitude];

            await api.put('/user/profile', { locationCoords });

            try {
                const res = await api.get('/user/nearby', {
                    params: {
                    lng: locationCoords[0],
                    lat: locationCoords[1],
                    // radiusKm: 100,
                    },
                });

                setTravellers(res.data);
            } catch (err) {
                console.error('Error fetching users nearby', err);
                openSnackbar('Failed to fetch nearby users', 'error');
            }

        } catch (err) {
            console.error('Error updating location or fetching users', err);
            setLocationPermissionDenied(true);
            if (err.message.includes('denied')) {
                openSnackbar('Please enable location access', 'error');    
            } else {
                openSnackbar('Geolocation error: ' + err.message, 'error');
            }
        } finally {
            setUsersLoading(false);
        }
    };

    const filteredPlaces = useMemo(() => {
        const categoryFilter = features.filter(p => selectedTypes.includes(p.properties.category));
        const countryFilter = categoryFilter.filter(p => selectedCountries.includes(p.properties.country));       
        if (showFavouritesOnly) {
            return countryFilter.filter(p => favouriteIds.includes(p.properties.id));
        }
        else {
            return countryFilter;
        }
    }, [features, selectedTypes, selectedCountries, showFavouritesOnly, favouriteIds]);

    const handleFavouriteClick = async (pinId) => {
        if (user === null) {
            isMobile ? navigate('/profile') : setDisplayLogin(true);
            return;
        }
        setFavouriteLoading(true);
        timer.current = window.setTimeout(async () => {
            try {
                const res = await api.post(`/user/favourites/${pinId}`);
                setFavouriteIds(res.data); 
            } catch (err) {
                console.error("Error toggling favourite", err);
                openSnackbar("Error saving favourite", 'error');
            } finally {
                setFavouriteLoading(false);
            }
        }, 500); 
    };

    const handleUserMapMarkerClick = async (userId) => {
        if (user === null) {
            isMobile ? navigate('/profile') : setDisplayLogin(true);
            return;
        }
        setSelectedTraveller({id: userId}); 
        setSelectedFeature(null);
    };

    const handleMarkerClick = (poi) => {
        setSelectedFeature(poi);
        setSelectedTraveller(null); 
    };

    if (!user) {
        return (
            <CallToActionHero
                title="PanAm Attractions"
                description="Login to access our curated collection of attractions and notable stops along the Pan-American Highway. From iconic landmarks to lesser-known local favorites worth the detour."
                video={MapFeatureVideo}
                />
        );
    }

    return (
        <MapPageLayout 
            usersLoading={usersLoading}
            filteredPlaces={filteredPlaces}
            toggleSelectedTypes={toggleSelectedTypes}
            mapFlyTo={flyTo}
            showDrawer={showDrawer}
            showDrawerView={showDrawerView}
            setShowDrawerView={setShowDrawerView}
            isDesktopViewExpanded={isDesktopViewExpanded}
            handleShowLocations={handleShowLocations}
            onToggleFavourites={() => setShowFavouritesOnly(!showFavouritesOnly)}
            footerHeight={props.footerHeight}
            locationPermissionDenied={locationPermissionDenied}
            updateLocationAndFetchUsers={updateLocationAndFetchUsers}
            setMobileDrawerState={setMobileDrawerState}>

            <Map
                onMarkerClick={(poi) => handleMarkerClick(poi)}
                handleUserClick={handleUserMapMarkerClick}
                isDesktopViewExpanded={isDesktopViewExpanded}
                mobileDrawerState={mobileDrawerState}
            />

            {selectedFeature && (
                <FeaturePopupCard
                    onClose={() => setSelectedFeature(null)}
                    onOpenDetails={() => setPlaceDetailsOpen(true)}
                    handleFavouriteClick={handleFavouriteClick}
                    isFavourite={favouriteIds.includes(selectedFeature?.id)}
                    favouriteRequestLoading={favouriteLoading}
                />  
            )}

            {placeDetailsOpen && (
                <FeatureDetailsModal 
                    onClose={() => setPlaceDetailsOpen(false)}
                    handleFavouriteClick={handleFavouriteClick}
                    isFavourite={favouriteIds.includes(selectedFeature?.id)}
                    favouriteRequestLoading={favouriteLoading} />
            )}

            {selectedTraveller && (
                <UserPopupCard
                    onClick={() => setUserDetailsOpen(true)}
                    onClose={() => setSelectedTraveller(null)}
                />
            )}

            {userDetailsOpen && (
                <UserDetailsModal 
                    onClose={() => setUserDetailsOpen(false)} 
                    mapFlyTo={flyTo}/>
            )}
            

            <DesktopProfileModal displayLogin={displayLogin} setDisplayLogin={setDisplayLogin}/>

        </MapPageLayout>
        
    );
}

function getAllowedTypes(userRole) {
    return userRole === 'Admin'
        ? ALL_TYPES
        : ALL_TYPES.filter(type => type !== 'Hidden Gems');
}